import React, { useMemo, useState, useEffect } from 'react';
import api from '../api';
import { Clock, Send, Search, User, MessageCircle } from 'lucide-react';
import ModeratorNavbar from './ModeratorNavbar';
import './ModeratorDashboard.css';

const ModeratorMessages = ({ onLogout }) => {
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [threadMessages, setThreadMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [searchUsers, setSearchUsers] = useState('');
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    setNotice('');
    
    try {
      const response = await api.get('/api/moderator/communications/users', {
        params: { search: searchUsers || '' }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // lightweight debounce
    const t = setTimeout(() => {
      loadUsers();
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUsers]);

  useEffect(() => {
    const loadReports = async () => {
      if (!selectedUserId) {
        setReports([]);
        setSelectedReportId(null);
        setThreadMessages([]);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await api.get(`/api/moderator/communications/user/${selectedUserId}/reports`);
        const nextReports = response.data.reports || [];
        setReports(nextReports);

        // Auto-select first thread
        if (nextReports.length > 0) {
          const first = nextReports[0];
          setSelectedReportId(first.reportId ?? null);
        } else {
          setSelectedReportId(null);
          setThreadMessages([]);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load report threads');
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [selectedUserId]);

  useEffect(() => {
    const loadThread = async () => {
      if (!selectedUserId) {
        setThreadMessages([]);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const params = {};
        if (selectedReportId) params.reportId = selectedReportId;
        const response = await api.get(`/api/moderator/communications/user/${selectedUserId}/thread`, { params });
        setThreadMessages(response.data.messages || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load thread');
      } finally {
        setLoading(false);
      }
    };

    loadThread();
  }, [selectedUserId, selectedReportId]);

  const handleSendMessage = async (userId) => {
    if (!messageText.trim()) {
      setNotice('');
      setError('Please enter a message');
      return;
    }

    try {
      setError('');
      setNotice('');
      await api.post(`/api/moderator/message/${userId}`, {
        message: messageText,
        requestType: 'info',
        reportId: selectedReportId || null
      });
      setNotice('Message sent successfully');
      setMessageText('');

      // Refresh thread + lists
      const params = {};
      if (selectedReportId) params.reportId = selectedReportId;
      const threadRes = await api.get(`/api/moderator/communications/user/${userId}/thread`, { params });
      setThreadMessages(threadRes.data.messages || []);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send message');
    }
  };

  const selectedUser = useMemo(() => {
    return users.find(u => u.userId === selectedUserId) || null;
  }, [users, selectedUserId]);

  const formatUserLabel = (u) => {
    if (!u) return '';
    return u.userId || 'Unknown user';
  };

  const formatThreadLabel = (t) => {
    if (!t) return 'Thread';
    if (t.reportId) {
      const type = t.reportType ? `${t.reportType}` : 'Report';
      const desc = t.reportDescription ? ` - ${t.reportDescription.substring(0, 42)}${t.reportDescription.length > 42 ? '...' : ''}` : '';
      return `${type}${desc}`;
    }
    return 'General';
  };

  return (
    <div className="moderator-shell">
      <ModeratorNavbar onLogout={onLogout} />

      {error && <div className="mod-alert">{error}</div>}

      {notice && <div className="mod-alert" style={{ borderLeftColor: '#28a745' }}>{notice}</div>}

      <div className="moderator-content">
        <h2 className="section-title">Moderator Messages</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 320px 1fr', gap: '12px', alignItems: 'stretch' }}>
          {/* Left: users */}
          <div className="mod-panel" style={{ padding: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search user id..."
                value={searchUsers}
                onChange={(e) => setSearchUsers(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e0e0e0' }}
              />
            </div>

            {loading && users.length === 0 ? (
              <div className="loading">Loading conversations...</div>
            ) : users.length === 0 ? (
              <div className="no-reports">No conversations found</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
                {users.map(u => (
                  <button
                    key={u.userId}
                    onClick={() => setSelectedUserId(u.userId)}
                    className="icon-btn neutral"
                    style={{
                      justifyContent: 'flex-start',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 10px',
                      borderRadius: '10px',
                      border: selectedUserId === u.userId ? '1px solid #97AC82' : '1px solid #eee',
                      background: selectedUserId === u.userId ? '#f3f7ef' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                        <User size={14} />
                        <span>{formatUserLabel(u)}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{u.userId}</div>
                      <div style={{ fontSize: '12px', color: '#777' }}>
                        {u.lastTimestamp ? new Date(u.lastTimestamp).toLocaleString() : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Middle: report threads */}
          <div className="mod-panel" style={{ padding: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <MessageCircle size={16} />
              <div style={{ fontWeight: 700 }}>Threads</div>
            </div>

            {!selectedUserId ? (
              <div className="no-reports">Select a user to view threads</div>
            ) : loading && reports.length === 0 ? (
              <div className="loading">Loading threads...</div>
            ) : reports.length === 0 ? (
              <div className="no-reports">No threads for this user</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
                {reports.map(r => {
                  const rid = r.reportId ?? null;
                  const active = (selectedReportId ?? null) === rid;
                  return (
                    <button
                      key={rid || 'general'}
                      onClick={() => setSelectedReportId(rid)}
                      className="icon-btn neutral"
                      style={{
                        justifyContent: 'flex-start',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 10px',
                        borderRadius: '10px',
                        border: active ? '1px solid #97AC82' : '1px solid #eee',
                        background: active ? '#f3f7ef' : 'white'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                        <div style={{ fontWeight: 600 }}>{formatThreadLabel(r)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {rid ? `Report: ${rid}` : 'No report linked'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#777' }}>
                          {r.lastTimestamp ? new Date(r.lastTimestamp).toLocaleString() : ''}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: thread */}
          <div className="mod-panel" style={{ padding: '12px', display: 'flex', flexDirection: 'column', minHeight: '65vh' }}>
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontWeight: 800 }}>Conversation</div>
              {selectedUserId && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  User: {formatUserLabel(selectedUser)} ({selectedUserId})
                </div>
              )}
              {selectedReportId && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Report: {selectedReportId}
                </div>
              )}
            </div>

            {!selectedUserId ? (
              <div className="no-reports">Select a user to view conversation</div>
            ) : loading && threadMessages.length === 0 ? (
              <div className="loading">Loading messages...</div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', paddingRight: '6px' }}>
                {threadMessages.length === 0 ? (
                  <div className="no-reports">No messages in this thread yet</div>
                ) : (
                  threadMessages.map(m => (
                    <div key={m.id} style={{
                      border: '1px solid #eee',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      marginBottom: '10px',
                      background: 'white'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
                        <div style={{ fontWeight: 700, fontSize: '12px', color: '#333' }}>Moderator</div>
                        <div style={{ fontSize: '12px', color: '#777', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={12} />
                          {new Date(m.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#222' }}>{m.message}</div>

                      {m.userResponse && (
                        <div style={{
                          marginTop: '10px',
                          padding: '8px 10px',
                          borderRadius: '10px',
                          background: '#f7f7f7',
                          border: '1px solid #e9e9e9'
                        }}>
                          <div style={{ fontWeight: 700, fontSize: '12px', color: '#333', marginBottom: '4px' }}>User response</div>
                          <div style={{ fontSize: '13px', color: '#222' }}>{m.userResponse}</div>
                          {m.respondedAt && (
                            <div style={{ fontSize: '12px', color: '#777', marginTop: '6px' }}>
                              Responded at: {new Date(m.respondedAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Composer */}
            {selectedUserId && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '10px' }}>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Write a message..."
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e0e0e0', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleSendMessage(selectedUserId)}>
                    <Send size={14} /> Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeratorMessages;
